import { HttpClient, HttpEvent, HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { formatISO } from 'date-fns';
import { environment } from 'environment/environment';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiParams, BaseApiService } from 'shared/services/base-api.service';
import { CONTROLLERS } from 'shared/services/controllers';
import {
  ApiResponse,
  ShipmentInfo,
  SiteLocation,
  Trailer,
  TrailerFiltersForm,
  TrailerFiltersRequest,
  TrailerHistory,
} from 'shared/types';
import { OptionsToParams } from 'shared/utility';

/**
 * @deprecated firefly-1-9758-ui will be removed after feature flag expires
 */
export interface GetTrailersBySiteOptions {
  trailerNumber?: string;
  scacCode?: string | null;
}

/**
 * @deprecated firefly-1-9758-ui will be removed after feature flag expires
 */
@Injectable({
  providedIn: 'root',
})
export class TrailersService extends BaseApiService<
  ApiResponse<{ data: Trailer[] }>,
  ApiResponse<Trailer>,
  ApiResponse<{ id: string }>
> {
  constructor(public override http: HttpClient) {
    super(http, environment.trailersApiBaseUrl);
  }

  public controllerName(): string {
    return CONTROLLERS.trailers;
  }

  public depart(trailer: Partial<Trailer>): Observable<ApiResponse<{ uuid: string }>> {
    return this.http.put<ApiResponse<{ uuid: string }>>(
      `${this.api}/${this.controllerName()}/depart/${trailer.uuid}`,
      trailer,
    );
  }

  public override update(
    id: string,
    entity: Trailer & { updatedByUi?: boolean },
  ): Observable<
    ApiResponse<{
      id: string;
    }>
  > {
    entity.updatedByUi = true;
    return super.update(id, entity);
  }

  public reArrive(trailer: Partial<Trailer>): Observable<ApiResponse<{ uuid: string }>> {
    return this.http.put<ApiResponse<{ uuid: string }>>(
      `${this.api}/${this.controllerName()}/departByMistake/${trailer.uuid}`,
      trailer,
    );
  }

  public getTrailerNumbersBySite(
    siteId: string,
    options: ApiParams<GetTrailersBySiteOptions>,
  ): Observable<
    ApiResponse<{
      data: {
        trailerNumber: string;
        disabled: boolean;
      }[];
    }>
  > {
    const params = OptionsToParams.processOptionsToParams(new HttpParams(), options);

    return this.http.get<
      ApiResponse<{
        data: {
          trailerNumber: string;
          disabled: boolean;
        }[];
      }>
    >(`${this.api}/${CONTROLLERS.sites}/${siteId}/trailer-numbers`, { params });
  }

  public getScacsBySite(
    siteId: string,
    trailerNumber?: string,
  ): Observable<
    ApiResponse<{
      scacs: {
        code: string;
        name: string;
        disabled: boolean;
      }[];
    }>
  > {
    let params = new HttpParams();
    if (trailerNumber) {
      params = params.append('trailerNumber', trailerNumber);
    }

    return this.http.get<
      ApiResponse<{
        scacs: {
          code: string;
          name: string;
          disabled: boolean;
        }[];
      }>
    >(`${this.api}/${CONTROLLERS.sites}/${siteId}/scacs`, { params });
  }

  public getTrailersBySite(
    siteId: string | null,
    filters?: Partial<TrailerFiltersRequest>,
    pagination?: { pageNumber: number; pageSize: number },
  ): Observable<ApiResponse<{ data: Trailer[] }>> {
    let params = new HttpParams();
    if (filters) {
      params = this.processFilters(filters, params);
    }
    if (pagination) {
      params = params.set('pageNumber', pagination.pageNumber.toString());
      params = params.set('pageSize', pagination.pageSize.toString());
    }

    return this.http.get<
      ApiResponse<{
        data: Trailer[];
      }>
    >(`${this.api}/${this.controllerName()}/${siteId}/list`, { params });
  }

  public getTrailersList(
    siteId: string | null,
    filters?: Partial<TrailerFiltersRequest>,
    pagination?: { pageNumber: number; pageSize: number },
  ): Observable<ApiResponse<{ data: Trailer[] }>> {
    const requestBody: Partial<TrailerFiltersRequest> & {
      pageNumber?: number;
      pageSize?: number;
    } = {};

    if (filters) {
      Object.assign(requestBody, filters);
    }
    if (pagination) {
      requestBody.pageNumber = pagination.pageNumber;
      requestBody.pageSize = pagination.pageSize;
    }

    return this.http.post<
      ApiResponse<{
        data: Trailer[];
      }>
    >(`${this.api}/${this.controllerName()}/${siteId}/list`, requestBody);
  }

  public getTrailersBySiteCSV(
    siteId: string | null,
    filters?: TrailerFiltersForm,
  ): Observable<{ progress: number; blob?: Blob; filename?: string }> {
    let params = new HttpParams();

    if (filters) {
      params = this.processFilters(filters, params);
    }

    return this.http
      .get(`${this.api}/${this.controllerName()}/${siteId}/export`, {
        params,
        responseType: 'blob' as 'json',
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event: HttpEvent<unknown>) => {
          switch (event.type) {
            case HttpEventType.DownloadProgress: {
              const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
              return { progress };
            }

            case HttpEventType.Response: {
              const blob = event.body as Blob;
              const filenameString = event.headers.get('content-disposition')?.split(';')[1];
              const filename = filenameString?.split('=')[1].trim() ?? 'trailers.csv';
              return { progress: event.status == 204 ? -1 : 100, blob, filename };
            }

            default:
              return { progress: 0 };
          }
        }),
      );
  }

  public downloadHistoryExport(
    trailerId: string,
    dateFrom: Date | null,
    dateTo?: Date | null,
  ): Observable<{ trailerHistoryData: Blob; fileName: string } | undefined> {
    let params = new HttpParams();
    if (dateFrom && dateTo) {
      params = params.set('startDate', dateFrom.toISOString());
      params = params.set('endDate', dateTo.toISOString());
    }

    return this.http
      .get(`${this.api}/${this.controllerName()}/${trailerId}/history/export`, {
        params,
        responseType: 'blob',
        observe: 'events',
      })
      .pipe(
        map((event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.Response) {
            const contentDisposition = event.headers.get('content-disposition');
            let fileName = 'history.csv'; // Default filename

            if (contentDisposition) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
              if (matches != null && matches[1]) {
                fileName = matches[1].replace(/['"]/g, '').trim(); // Remove quotes
              } else {
                const utf8Match = /filename\*=UTF-8''([^;]+)/.exec(contentDisposition);
                if (utf8Match != null && utf8Match[1]) {
                  fileName = decodeURIComponent(utf8Match[1].trim());
                }
              }
            }

            return { trailerHistoryData: event.body as Blob, fileName };
          }
          return undefined;
        }),
      );
  }

  public getHistory(
    siteId: string,
    trailerId: string,
    dateFrom: Date | null,
    dateTo?: Date | null,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<ApiResponse<{ data: TrailerHistory[] }>> {
    const params = this.getHistoryParams(siteId, dateFrom, dateTo, pageNumber, pageSize);
    return this.http.get<ApiResponse<{ data: TrailerHistory[] }>>(
      `${this.api}/${this.controllerName()}/${trailerId}/history`,
      {
        params,
      },
    );
  }

  private getHistoryParams(
    siteId: string,
    dateFrom: Date | null,
    dateTo?: Date | null,
    pageNumber = 1,
    pageSize = 100,
  ): HttpParams {
    let params = new HttpParams();
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());
    params = params.set('siteId', siteId);
    if (dateFrom && dateTo) {
      params = params.set('dateFrom', formatISO(dateFrom));
      params = params.set('dateTo', formatISO(dateTo));
    }
    return params;
  }

  private processFilters(filters: TrailerFiltersForm, params: HttpParams): HttpParams {
    if (!filters) return params;

    Object.entries(filters).forEach(([key, value]) => {
      let values = [];
      if (value === undefined || value === null) return;
      if (typeof value === 'boolean') {
        params = params.append(key, value.toString());
      } else {
        if (Array.isArray(value)) {
          values = value;
        } else if (value && typeof value === 'object') {
          values = Object.values(value);
        } else {
          values = [value];
        }

        values
          .filter((x) => x !== null && x !== undefined && x !== '')
          .forEach((value) => {
            params = params.append(key, value as string);
          });
      }
    });
    return params;
  }

  public getSiteShipments(siteId: string): Observable<ApiResponse<{ shipmentInfo: ShipmentInfo[] }>> {
    return this.http.get<ApiResponse<{ shipmentInfo: ShipmentInfo[] }>>(
      `${this.api}/${CONTROLLERS.sites}/${siteId}/trailers/arrived/shipment-numbers`,
    );
  }

  public getTrailerLocations(siteId: string, trailerId: string | null = null): Observable<ApiResponse<SiteLocation[]>> {
    let params = new HttpParams();
    if (trailerId) {
      params = params.set('trailerId', trailerId);
    }
    return this.http.get<ApiResponse<SiteLocation[]>>(`${this.api}/${CONTROLLERS.locations}/${siteId}`, { params });
  }

  public importCsv(siteId: string, file: File): Observable<ApiResponse<{ imported: boolean; importedAt: Date }>> {
    const formData = new FormData();
    formData.append('csvFile', file);
    formData.append('siteId', siteId);
    return this.http.post<
      ApiResponse<{
        imported: boolean;
        importedAt: Date;
      }>
    >(`${this.api}/${this.controllerName()}/import/csv`, formData);
  }
}
